/**
 * cart-seam-analytics.js — mdq-324 Step 1 measurement emitters.
 *
 * Telemetry only; zero visible UX change. Emits five GA4 events to
 * window.dataLayer for the cart -> checkout seam:
 *   cart_view, cart_abandon, checkout_cta_click,
 *   shipping_estimate_shown, builder_line_item_render
 *
 * Purely additive: the existing add_to_cart (GA4-native / Shopify) and
 * begin_checkout (GTM tag 50, vendored in snippets/ultimate-datalayer.liquid)
 * emitters are untouched. This module never calls preventDefault /
 * stopPropagation, so those handlers keep firing exactly as today. GTM
 * tags/triggers for these events live in the openClawAgency workspace
 * 'k1-seam-fix-measurement (mdq-324)' per the analytics boundary in AGENTS.md.
 *
 * Loaded (deferred) by sections/cart-drawer.liquid and sections/main-cart.liquid.
 */
(function () {
  'use strict';

  // (1) Re-entry guard + dataLayer bootstrap.
  if (window.__mdCartSeamAnalytics) return;
  window.__mdCartSeamAnalytics = true;
  window.dataLayer = window.dataLayer || [];

  // (2) State — parse the #CartSeamState island, bail to safe defaults.
  var DEFAULTS = {
    surface: null,
    item_count: 0,
    total_price: 0,
    contains_builder_config: false,
    free_shipping_threshold: null,
  };

  function readIslandState() {
    var node = document.getElementById('CartSeamState');
    if (!node) return DEFAULTS;
    try {
      var parsed = JSON.parse(node.textContent || '{}');
      return {
        surface: parsed.surface != null ? parsed.surface : null,
        item_count: parsed.item_count != null ? parsed.item_count : 0,
        total_price: parsed.total_price != null ? parsed.total_price : 0,
        contains_builder_config: !!parsed.contains_builder_config,
        free_shipping_threshold:
          parsed.free_shipping_threshold != null ? parsed.free_shipping_threshold : null,
      };
    } catch (e) {
      return DEFAULTS;
    }
  }

  var island = readIslandState();

  var cartState = {
    itemCount: island.item_count,
    totalCents: island.total_price,
    containsBuilder: island.contains_builder_config,
    thresholdCents: island.free_shipping_threshold,
  };

  var surface = island.surface;

  // Session vars.
  var openedAt = null;
  var viewFired = false; // per open
  var checkoutIntent = false;
  var pendingAbandon = null;
  var abandonTimer = null;
  var lastPointerDown = { el: null, t: 0 };
  var lastPopstateAt = 0;

  // (4) Helpers.
  function push(obj) {
    window.dataLayer.push(obj);
  }

  function eur(cents) {
    return Number((cents / 100).toFixed(2));
  }

  function cartValue() {
    return eur(cartState.totalCents);
  }

  function cancelPendingAbandon() {
    pendingAbandon = null;
    if (abandonTimer) {
      clearTimeout(abandonTimer);
      abandonTimer = null;
    }
  }

  // (3) Refresh listener — cart.js dispatches cart:updated with the full
  // cart JSON after every AJAX mutation.
  document.addEventListener('cart:updated', function (e) {
    var cart = e && e.detail && e.detail.cart;
    if (!cart) return;
    cartState.itemCount = cart.item_count != null ? cart.item_count : cartState.itemCount;
    cartState.totalCents = cart.total_price != null ? cart.total_price : cartState.totalCents;
    var items = cart.items || [];
    cartState.containsBuilder = items.some(function (it) {
      return it && it.properties && (it.properties._build_id || it.properties._drawer_size);
    });

    // Containers were innerHTML-replaced (cart.js:136-167); re-scan builder
    // rows if the cart surface is currently visible. Do NOT re-fire cart_view.
    if (isSurfaceVisible()) {
      scan();
    }
  });

  // Is the cart surface currently on screen?
  function isSurfaceVisible() {
    if (surface === 'page') return true;
    var drawerEl = document.querySelector('cart-drawer');
    return !!(drawerEl && drawerEl.hasAttribute('open'));
  }

  // Shipping note visibility — the note is static made-to-order copy.
  function shippingNoteVisible() {
    if (surface === 'page') {
      return !!document.querySelector('.shipping-speed-note');
    }
    // Drawer surface: the note lives in the drawer footer.
    var footer = document.querySelector('div[id^="CartDrawerFooter-"]');
    if (!footer) return false;
    if (footer.classList.contains('hidden')) return false;
    return !!footer.querySelector('.shipping-speed-note');
  }

  // (7) builder_line_item_render scan.
  function surfaceContainer() {
    if (surface === 'page') {
      return document.querySelector('main-cart');
    }
    return document.querySelector('div[id^="CartDrawerBody-"]');
  }

  function scan() {
    var root = surfaceContainer();
    if (!root) return;
    var rows = root.querySelectorAll('.cart-bundle .cart-item');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var grp = row.closest ? row.closest('.cart-bundle') : null;
      var titleEl = row.querySelector('.cart-item__title');
      var metaEl = grp ? grp.querySelector('.cart-bundle__meta') : null;
      push({
        event: 'builder_line_item_render',
        line_item_title: titleEl ? titleEl.textContent.trim() : '',
        has_config_summary: !!(metaEl && metaEl.textContent.trim().length),
        has_render_thumb: !!row.querySelector('.cart-item__media img'),
      });
    }
  }

  // (5) cart_view + session open.
  function openSession() {
    if (viewFired) return; // single-fire guard per open
    viewFired = true;
    openedAt = Date.now();
    checkoutIntent = false;
    cancelPendingAbandon();

    var noteVisible = shippingNoteVisible();

    push({
      event: 'cart_view',
      path: location.pathname,
      line_items_count: cartState.itemCount,
      cart_value: cartValue(),
      contains_builder_config: cartState.containsBuilder,
      shipping_estimate_shown: noteVisible,
    });

    if (noteVisible) {
      push({
        event: 'shipping_estimate_shown',
        shipping_eur: null,
        subtotal_eur: eur(cartState.totalCents),
        threshold_delta_eur:
          cartState.thresholdCents == null
            ? null
            : eur(cartState.thresholdCents - cartState.totalCents),
      });
    }

    scan();
  }

  // (10) cart_abandon — close/navigate away without begin_checkout within 30s.
  function closeSession() {
    viewFired = false; // allow the next open to fire again
    if (checkoutIntent) return;

    var reason = 'close_button';
    if (lastPointerDown.el && Date.now() - lastPointerDown.t < 1000) {
      var el = lastPointerDown.el;
      if (el.closest && el.closest('.fixed-overlay')) {
        reason = 'outside_click';
      } else if (el.closest && el.closest('.drawer__close-btn')) {
        reason = 'close_button';
      } else {
        reason = 'close_button';
      }
    }

    pendingAbandon = {
      event: 'cart_abandon',
      abandon_reason: reason,
      time_on_cart_sec: Math.round((Date.now() - openedAt) / 1000),
      cart_value: cartValue(),
    };
    abandonTimer = setTimeout(flushPendingAbandon, 30000);
  }

  function flushPendingAbandon() {
    if (pendingAbandon) {
      push(pendingAbandon);
      pendingAbandon = null;
    }
    if (abandonTimer) {
      clearTimeout(abandonTimer);
      abandonTimer = null;
    }
  }

  // (6) Drawer observation via the theme's 'open' attribute (source of truth).
  var drawerEl = document.querySelector('cart-drawer');
  if (drawerEl && typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var rec = records[i];
        if (rec.attributeName !== 'open') continue;
        var wasOpen = rec.oldValue !== null; // attributeOldValue: true
        var isOpen = drawerEl.hasAttribute('open');
        if (isOpen && !wasOpen) {
          openSession();
        } else if (!isOpen && wasOpen) {
          closeSession();
        }
      }
    });
    observer.observe(drawerEl, {
      attributes: true,
      attributeFilter: ['open'],
      attributeOldValue: true,
    });
  }

  // /cart page: script is deferred, DOM is parsed — open immediately.
  if (surface === 'page') {
    openSession();
  }

  // (8) checkout_cta_click — document-delegated (footers are innerHTML-replaced).
  document.addEventListener('click', function (e) {
    var cta = e.target && e.target.closest && e.target.closest('[data-checkout-cta]');
    if (!cta) return;
    push({
      event: 'checkout_cta_click',
      cta_location: cta.getAttribute('data-cta-location') || 'page',
      cta_variant: cta.getAttribute('data-cta-variant') || 'v0',
      cart_value: cartValue(),
    });
    checkoutIntent = true;
    cancelPendingAbandon();
  });

  // (9) Checkout-intent suppression + pointer tracking.
  document.addEventListener('pointerdown', function (e) {
    var target = e.target;
    lastPointerDown = { el: target, t: Date.now() };
    if (
      target &&
      target.closest &&
      target.closest(
        'input[name="checkout"], button[name="checkout"], a[href="/checkout"], .additional-checkout-buttons, [data-checkout-cta]'
      )
    ) {
      checkoutIntent = true;
      cancelPendingAbandon();
    }
  });

  // (10) popstate tracking for back_nav classification.
  window.addEventListener('popstate', function () {
    lastPopstateAt = Date.now();
  });

  // (10) pagehide — flush best-effort; the 30s grace cannot outlive the page.
  window.addEventListener('pagehide', function () {
    if (pendingAbandon) {
      push(pendingAbandon);
      pendingAbandon = null;
      return;
    }

    var sessionOpen = surface === 'page' || isSurfaceVisible();
    if (!sessionOpen || checkoutIntent) return;

    var reason;
    if (Date.now() - lastPopstateAt < 2000) {
      reason = 'back_nav';
    } else if (
      lastPointerDown.el &&
      Date.now() - lastPointerDown.t < 2000 &&
      lastPointerDown.el.closest &&
      lastPointerDown.el.closest('a,button')
    ) {
      reason = 'route_change';
    } else {
      reason = 'back_nav';
    }

    push({
      event: 'cart_abandon',
      abandon_reason: reason,
      time_on_cart_sec: Math.round((Date.now() - openedAt) / 1000),
      cart_value: cartValue(),
    });
  });
})();
